import { useNavigate } from "react-router-dom";
import { Plus, GraduationCap } from "lucide-react";
import { motion } from "framer-motion";
import teacherHero from "../assets/images/teacher-classes.jpeg";

type ClassSummary = {
  id: number;
  code: string;
  title: string;
  term: string;
  students: number;
};

// Mock classes – in real app this would come from Supabase
const classes: ClassSummary[] = [
  { id: 1, code: "CS101", title: "Intro to Computer Science", term: "Spring 2025", students: 42 },
  { id: 2, code: "MATH204", title: "Linear Algebra", term: "Spring 2025", students: 30 },
  { id: 3, code: "ENG310", title: "Modern Poetry", term: "Autumn 2025", students: 18 },
];

export default function ClassesPage() {
  const navigate = useNavigate();

  return (
    <div className="space-y-10">
      {/* Hero banner */}
      <section
        className="relative overflow-hidden rounded-2xl bg-gray-900 text-white shadow-sm"
        style={{ backgroundImage: `url(${teacherHero})`, backgroundSize: "cover" }}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

        <div className="relative z-10 flex flex-col gap-6 p-8 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold">Your Courses</h1>
            <p className="mt-2 max-w-md text-sm text-gray-200">
              Overview of all active &amp; upcoming courses. Click a card to drill
              in.
            </p>
            <button
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur transition hover:bg-white/20"
              onClick={() => navigate("/app/courses/new")}
            >
              <Plus size={16} /> New course
            </button>
          </div>

          {/* Quick stat */}
          <div className="flex items-center gap-3 rounded-lg bg-white/10 px-4 py-3 text-sm font-medium backdrop-blur">
            <GraduationCap size={18} /> {classes.length} total courses
          </div>
        </div>

        <p className="absolute bottom-2 right-4 text-[10px] text-gray-300">
          Photo by{' '}
          <a
            href="https://www.pexels.com/@haidar-azmi-2148111388"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Haidar Azmi
          </a>{' '}
          on{' '}
          <a href="https://www.pexels.com" target="_blank" rel="noreferrer" className="underline">
            Pexels
          </a>
        </p>
      </section>

      {/* Cards */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {classes.map((c) => (
          <motion.button
            key={c.id}
            onClick={() => navigate(`/app/courses/${c.id}`)}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.4 }}
            className="flex flex-col rounded-xl bg-white p-5 text-left shadow-sm transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/60"
          >
            <div className="flex-1">
              <p className="text-sm font-medium text-primary">{c.code}</p>
              <h2 className="mt-1 line-clamp-2 text-lg font-semibold text-gray-800">
                {c.title}
              </h2>
              <p className="mt-2 text-sm text-gray-600">{c.term}</p>
            </div>
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-gray-500">Students</span>
              <span className="font-medium text-gray-800">{c.students}</span>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}
